import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { adminApi, NotificationTemplateItem } from '../services/adminApi';
import { AdminShell } from '../components/admin/AdminShell';
import { theme, neutral, blueGrey, jadeGreen, goldenTopaz } from '../design-system/theme';
import '../design-system/styles.css';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageSubtitle,
  PrimaryButton,
  DangerButton,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  FormGroup,
  Label,
  Input,
  Textarea,
  AlertMessage,
  LoadingSpinner,
  HelpText,
} from '../styles/adminShared';

// ============================================================================
// Constants
// ============================================================================

const TEMPLATE_TYPES = [
  { key: 'CONSENT_CONFIRMATION', label: 'Consent Confirmation', description: 'Sent after a data owner grants consent.' },
  { key: 'RECONSENT_REQUEST', label: 'Reconsent Request', description: 'Sent when re-consent is required from a data owner.' },
  { key: 'EXPIRY_WARNING', label: 'Expiry Warning', description: 'Sent before consent is about to expire.' },
  { key: 'WITHDRAWAL_CONFIRMATION', label: 'Withdrawal Confirmation', description: 'Sent after consent withdrawal is processed.' },
] as const;

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'Hindi' },
  { code: 'bn', label: 'Bengali' },
  { code: 'gu', label: 'Gujarati' },
  { code: 'kn', label: 'Kannada' },
  { code: 'ml', label: 'Malayalam' },
  { code: 'mr', label: 'Marathi' },
  { code: 'pa', label: 'Punjabi' },
  { code: 'ta', label: 'Tamil' },
  { code: 'te', label: 'Telugu' },
] as const;

const PLACEHOLDERS: Record<string, string[]> = {
  CONSENT_CONFIRMATION: [
    '{{dataOwnerName}}',
    '{{consentId}}',
    '{{templateName}}',
    '{{grantedDate}}',
    '{{expiryDate}}',
    '{{organisationName}}',
  ],
  RECONSENT_REQUEST: [
    '{{dataOwnerName}}',
    '{{templateName}}',
    '{{originalConsentDate}}',
    '{{reconsentUrl}}',
    '{{organisationName}}',
  ],
  EXPIRY_WARNING: [
    '{{dataOwnerName}}',
    '{{consentId}}',
    '{{templateName}}',
    '{{expiryDate}}',
    '{{daysRemaining}}',
    '{{renewUrl}}',
    '{{organisationName}}',
  ],
  WITHDRAWAL_CONFIRMATION: [
    '{{dataOwnerName}}',
    '{{consentId}}',
    '{{templateName}}',
    '{{withdrawalDate}}',
    '{{organisationName}}',
  ],
};

// ============================================================================
// Page-specific styled components
// ============================================================================

const EditorLayout = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 24px;
  margin-top: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const TemplateSidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TemplateTypeButton = styled.button<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px;
  border: 2px solid ${props => props.$active ? blueGrey[5] : neutral[3]};
  border-radius: 10px;
  background: ${props => props.$active ? blueGrey[1] : 'white'};
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;

  &:hover {
    border-color: ${blueGrey[4]};
    background: ${neutral[1]};
  }
`;

const TemplateTypeName = styled.div<{ $active?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$active ? blueGrey[8] : theme.textPrimary};
`;

const TemplateTypeDesc = styled.div`
  font-size: 12px;
  color: ${theme.textSecondary};
`;

const EditorPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const LanguageTabs = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid ${neutral[3]};
  overflow-x: auto;
`;

const LanguageTab = styled.button<{ $active?: boolean; $hasOverride?: boolean }>`
  padding: 10px 16px;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: ${props => props.$active ? theme.primary : theme.textSecondary};
  border-bottom: 2px solid ${props => props.$active ? theme.primary : 'transparent'};
  margin-bottom: -2px;
  transition: all 0.2s;
  white-space: nowrap;
  position: relative;

  &:hover {
    color: ${theme.primary};
  }

  ${props => props.$hasOverride && !props.$active && `
    &::after {
      content: '';
      position: absolute;
      top: 8px;
      right: 6px;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: ${jadeGreen[5]};
    }
  `}
`;

const PlaceholderList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const PlaceholderTag = styled.code`
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 12px;
  background: ${neutral[2]};
  padding: 4px 8px;
  border-radius: 4px;
  color: ${blueGrey[7]};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${blueGrey[1]};
    color: ${blueGrey[9]};
  }
`;

const EditorActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid ${neutral[3]};
`;

const OverrideIndicator = styled.div<{ $isOverride: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.$isOverride ? goldenTopaz[1] : neutral[2]};
  color: ${props => props.$isOverride ? goldenTopaz[8] : theme.textSecondary};
`;

const EmailBodyTextarea = styled(Textarea)`
  min-height: 200px;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 13px;
`;

// ============================================================================
// Component
// ============================================================================

export const NotificationTemplateEditor: React.FC = () => {
  const [templates, setTemplates] = useState<NotificationTemplateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [selectedType, setSelectedType] = useState<string>(TEMPLATE_TYPES[0].key);
  const [selectedLang, setSelectedLang] = useState<string>(LANGUAGES[0].code);

  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [smsBody, setSmsBody] = useState('');

  const buildKey = (type: string, lang: string) => `${type}#${lang}`;

  const findTemplate = useCallback(
    (type: string, lang: string): NotificationTemplateItem | undefined => {
      const key = buildKey(type, lang);
      return templates.find(t => t.templateKey === key);
    },
    [templates]
  );

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const result = await adminApi.getNotificationTemplates();
      setTemplates(result);
    } catch (err) {
      setError('Failed to load notification templates.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // When selection changes, populate form fields from existing template or clear
  useEffect(() => {
    const existing = findTemplate(selectedType, selectedLang);
    if (existing) {
      setEmailSubject(existing.emailSubject || '');
      setEmailBody(existing.emailBody || '');
      setSmsBody(existing.smsBody || '');
    } else {
      setEmailSubject('');
      setEmailBody('');
      setSmsBody('');
    }
    setError(null);
    setSuccess(null);
  }, [selectedType, selectedLang, findTemplate]);

  const handleSave = async () => {
    const key = buildKey(selectedType, selectedLang);
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await adminApi.updateNotificationTemplate(key, { emailSubject, emailBody, smsBody });
      setSuccess('Template saved successfully.');
      await loadTemplates();
    } catch (err) {
      setError('Failed to save template.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    const key = buildKey(selectedType, selectedLang);
    if (!findTemplate(selectedType, selectedLang)) {
      return; // Nothing to reset
    }
    if (!window.confirm('Are you sure you want to reset this template to the system default? This will delete your custom override.')) {
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await adminApi.deleteNotificationTemplate(key);
      setSuccess('Template reset to system default.');
      await loadTemplates();
    } catch (err) {
      setError('Failed to reset template.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const copyPlaceholder = (placeholder: string) => {
    navigator.clipboard.writeText(placeholder).catch(() => {
      // Fallback - do nothing
    });
  };

  const isOverride = !!findTemplate(selectedType, selectedLang);
  const currentPlaceholders = PLACEHOLDERS[selectedType] || [];

  return (
    <AdminShell>
      <PageContainer>
        <PageHeader>
          <div>
            <PageTitle>Notification Templates</PageTitle>
            <PageSubtitle>
              Customise email and SMS notification templates for each type and language.
            </PageSubtitle>
          </div>
        </PageHeader>

        {error && <AlertMessage $variant="error">{error}</AlertMessage>}
        {success && <AlertMessage $variant="success">{success}</AlertMessage>}

        {loading ? (
          <LoadingSpinner>Loading templates...</LoadingSpinner>
        ) : (
          <EditorLayout>
            {/* Left sidebar - template type selection */}
            <TemplateSidebar>
              {TEMPLATE_TYPES.map(tt => {
                const hasAnyOverride = LANGUAGES.some(lang => findTemplate(tt.key, lang.code));
                return (
                  <TemplateTypeButton
                    key={tt.key}
                    $active={selectedType === tt.key}
                    onClick={() => setSelectedType(tt.key)}
                  >
                    <TemplateTypeName $active={selectedType === tt.key}>
                      {tt.label}
                      {hasAnyOverride && ' *'}
                    </TemplateTypeName>
                    <TemplateTypeDesc>{tt.description}</TemplateTypeDesc>
                  </TemplateTypeButton>
                );
              })}
            </TemplateSidebar>

            {/* Right panel - editor */}
            <EditorPanel>
              <Card>
                <CardHeader>
                  <CardTitle>
                    {TEMPLATE_TYPES.find(t => t.key === selectedType)?.label}
                  </CardTitle>
                  <OverrideIndicator $isOverride={isOverride}>
                    {isOverride ? 'Custom Override' : 'System Default'}
                  </OverrideIndicator>
                </CardHeader>

                <LanguageTabs>
                  {LANGUAGES.map(lang => (
                    <LanguageTab
                      key={lang.code}
                      $active={selectedLang === lang.code}
                      $hasOverride={!!findTemplate(selectedType, lang.code)}
                      onClick={() => setSelectedLang(lang.code)}
                    >
                      {lang.label}
                    </LanguageTab>
                  ))}
                </LanguageTabs>

                <CardBody>
                  <FormGroup>
                    <Label>Available Placeholders</Label>
                    <HelpText>Click a placeholder to copy it to your clipboard.</HelpText>
                    <PlaceholderList>
                      {currentPlaceholders.map(ph => (
                        <PlaceholderTag key={ph} onClick={() => copyPlaceholder(ph)} title="Click to copy">
                          {ph}
                        </PlaceholderTag>
                      ))}
                    </PlaceholderList>
                  </FormGroup>

                  <FormGroup>
                    <Label>Email Subject</Label>
                    <Input
                      value={emailSubject}
                      onChange={e => setEmailSubject(e.target.value)}
                      placeholder="Enter email subject line..."
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Email Body (HTML)</Label>
                    <EmailBodyTextarea
                      value={emailBody}
                      onChange={e => setEmailBody(e.target.value)}
                      placeholder="Enter HTML email body with {{placeholders}}..."
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>SMS Body</Label>
                    <Textarea
                      value={smsBody}
                      onChange={e => setSmsBody(e.target.value)}
                      placeholder="Enter SMS message text with {{placeholders}}..."
                      style={{ minHeight: '80px' }}
                    />
                  </FormGroup>

                  <EditorActions>
                    {isOverride && (
                      <DangerButton onClick={handleReset} disabled={saving}>
                        Reset to Default
                      </DangerButton>
                    )}
                    <PrimaryButton onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving...' : 'Save Template'}
                    </PrimaryButton>
                  </EditorActions>
                </CardBody>
              </Card>
            </EditorPanel>
          </EditorLayout>
        )}
      </PageContainer>
    </AdminShell>
  );
};

export default NotificationTemplateEditor;
